import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { map, Observable, of, switchMap } from 'rxjs';

import { Store } from '@ngrx/store';

import { AwsService } from '../../../../core/services/aws.service';
import { MessagingEnvironmentService } from '../../../../core/services/messaging-environment.service';
import { AuthenticationService } from '../../../user/services/authentication.service';
import { ApiResponse } from '../../interfaces/commons';
import { IAttachment } from '../../interfaces/IAttachment';
import { DealerSettingName, IDealerSetting } from '../../interfaces/IDealerSettings';
import { IMessageableEntity } from '../../interfaces/IMessageableEntity';
import { ITag } from '../../interfaces/ITag';
import { WorkspaceFeatures } from '../../interfaces/IWorkspace';
import { setUsers } from '../../store/chat.actions';
import { AppStateWithChat } from '../../store/chat.reducer';
import { PermissionsService } from '../permissions.service';
import { MessagingApiStateService } from './messaging-api-state.service';

@Injectable()
export class UsersApiService {
  constructor(
    private httpClient: HttpClient,
    private env: MessagingEnvironmentService,
    private state: MessagingApiStateService,
    private store: Store<AppStateWithChat>,
    private authenticationService: AuthenticationService,
    private awsService: AwsService,
    private permissionsService: PermissionsService
  ) {}

  getUsersByDefect(): Observable<IMessageableEntity[]> {
    if (!this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setChannelOwnerByUserId])) {
      return of([]);
    }
    if (this.state.users?.length) {
      return of(this.state.users);
    }
    return this.searchUsers({ searchTerm: '', page: 1, size: 10000, tags: [] }).pipe(
      map((users) => {
        this.store.dispatch(setUsers({ users }));
        return users;
      })
    );
  }

  searchUsers(data: {
    searchTerm: string;
    page: number;
    size: number;
    tags?: ITag[];
    useGroup?: boolean;
    entityUID?: string;
    status?: string;
  }): Observable<IMessageableEntity[]> {
    let groupName: IDealerSetting | undefined;
    let group: { uid: string; displayName: string } | undefined;
    if (this.state.user && this.state.user?.dealerSettings?.length) {
      groupName = this.state.user.dealerSettings.find((e) => e.key === DealerSettingName.truConnectGroupName);
      if (this.state.user?.subAccountGroups?.length) {
        group = this.state.user.subAccountGroups.find((e) => groupName?.value === e.displayName);
      }
    }
    const body: {
      accountUID: string;
      subAccountUID: string;
      searchTerm?: string;
      systemTags?: string[];
      groupUID?: string;
      module?: string;
      status?: string;
      businessReference?: {
        entityType: string;
        entityUID: string;
      };
    } = {
      accountUID: this.state.user?.accountUID ?? '',
      subAccountUID: this.state.user?.subAccountMessageableEntity?.subAccountUID || this.state.user?.subAccountUID || '',
      module: this.state.selectedWorkSpace?.id,
    };
    if (data.tags?.length) {
      body.systemTags = data.tags.map((e) => e.displayName);
    }

    if (data.searchTerm) {
      body.searchTerm = data.searchTerm;
    }

    if (data.status) {
      body.status = data.status;
    }

    if (group?.uid && data.useGroup) {
      body.groupUID = group.uid;
    }

    let url = `${this.env.env.apiUrl + this.env.env.messagingApiPath}/messageable-entity/search?sort=displayName:asc`;
    if (data.page) {
      url = url + `&page=${data.page}`;
    }
    if (data.size) {
      url = url + `&size=${data.size}`;
    }

    if (data.entityUID) {
      body.businessReference = {
        entityType: 'what',
        entityUID: data.entityUID,
      };
    }
    return this.httpClient.post<ApiResponse<IMessageableEntity>>(url, body).pipe(
      map((e) => {
        return e.documents;
      })
    );
  }

  updateUser(displayName: string, attachments: IAttachment[]): Observable<unknown> {
    return this.awsService.uploadFiles(attachments).pipe(
      switchMap((attachments) => {
        const body: {
          accountUID: string;
          subAccountUID: string;
          messageableEntityUID: string;
          displayName: string;
          profileImageURL?: string;
        } = {
          accountUID: this.state.user?.accountUID ?? '',
          subAccountUID: this.state.user?.subAccountMessageableEntity?.subAccountUID ?? '',
          messageableEntityUID: this.state.user?.subjectMessageableEntity?.uid ?? '',
          displayName: displayName,
        };

        if (attachments.length) {
          body.profileImageURL = attachments[0].thumbnailURL as string;
        }
        return this.httpClient.patch<unknown>(
          `${this.env.env.apiUrl + this.env.env.messagingApiPath}/messageable-entity`,
          body
        );
      })
    );
  }

  sendAway(to: Date, userId: string, message: string): Observable<unknown> {
    let awaySettings: { endDate: Date; delegateUID?: string; customMessage?: string };
    if (userId) {
      awaySettings = {
        endDate: to,
        delegateUID: userId,
      };
    } else {
      awaySettings = {
        endDate: to,
        customMessage: message,
      };
    }

    return this.httpClient.patch(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/messageable-entity/${
        this.state.user?.subjectMessageableEntity?.uid
      }/status`,
      {
        status: 'AWAY',
        awaySettings,
      }
    );
  }

  cancelAway(): Observable<unknown> {
    return this.httpClient.patch(
      `${this.env.env.apiUrl + this.env.env.messagingApiPath}/messageable-entity/${
        this.state.user?.subjectMessageableEntity?.uid
      }/status`,
      {
        status: 'ACTIVE',
      }
    );
  }

  getUser() {
    return this.authenticationService.getUserProfile();
  }

  getMemberUID(members: number[]): Observable<string[]> {
    return this.httpClient
      .post<{ uid: string }[]>(
        `${this.env.env.apiUrl + this.env.env.messagingApiPath}/messageable-entity/search-by-entity-uid`,
        {
          subIDs: members.map((e) => e.toString()),
        }
      )
      .pipe(
        map((members) => {
          return members.map((e) => e.uid);
        })
      );
  }
}
